import { useEffect, useState } from "react";
import { DOC_SECTIONS, loadDoc, type DocSection, type DocTier } from "../../content/docs/docsManifest";

type DocState = {
    markdown: string | null;
    loading: boolean;
    error: Error | null;
};

/**
 * Resolve one section's markdown for a tier.
 *
 * The cancellation flag matters because switching sections in the sidebar
 * starts a new lazy chunk fetch while the previous one may still be in flight;
 * without it a slow first request can overwrite the newer document.
 */
export const useDocMarkdown = (section: DocSection | undefined, tier: DocTier): DocState => {
    const [state, setState] = useState<DocState>({ markdown: null, loading: Boolean(section), error: null });

    useEffect(() => {
        if (!section) {
            setState({ markdown: null, loading: false, error: null });
            return;
        }

        let cancelled = false;
        setState({ markdown: null, loading: true, error: null });

        loadDoc(section, tier)
            .then((markdown) => {
                if (!cancelled) setState({ markdown, loading: false, error: null });
            })
            .catch((error: Error) => {
                if (!cancelled) setState({ markdown: null, loading: false, error });
            });

        return () => {
            cancelled = true;
        };
    }, [section, tier]);

    return state;
};

type AllDocsState = {
    docs: { section: DocSection; markdown: string }[] | null;
    loading: boolean;
    error: Error | null;
};

/** Every section's markdown for one tier, in manifest order — used by the print routes. */
export const useAllDocsMarkdown = (tier: DocTier): AllDocsState => {
    const [state, setState] = useState<AllDocsState>({ docs: null, loading: true, error: null });

    useEffect(() => {
        let cancelled = false;
        setState({ docs: null, loading: true, error: null });

        Promise.all(
            DOC_SECTIONS.map(async (section) => ({
                section,
                markdown: await loadDoc(section, tier),
            })),
        )
            .then((docs) => {
                if (!cancelled) setState({ docs, loading: false, error: null });
            })
            .catch((error: Error) => {
                if (!cancelled) setState({ docs: null, loading: false, error });
            });

        return () => {
            cancelled = true;
        };
    }, [tier]);

    return state;
};
